// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface Advisor {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  brokerageId: string;
  brokerageName?: string;
  phoneNumber?: string;
  licenseNumber?: string;
  photoUrl?: string;
  notes?: string;
  keycloakUserId?: string;
  status: 'invited' | 'active' | 'inactive' | 'suspended' | 'terminated' | 'pending_activation';
  role: 'ADMIN' | 'USER';
  invitedAt?: string;
  activatedAt?: string;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface AdvisorInvite {
  email: string;
  firstName: string;
  lastName: string;
  brokerageId: string;
  phoneNumber?: string;
  licenseNumber?: string;
  role: 'ADMIN' | 'USER';
  notes?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

const API_BASE = '/api/advisors';

export const advisorApi = {
  /**
   * Get all advisors with pagination
   */
  getAll: async (page = 0, size = 20, sort = 'createdAt', direction = 'DESC'): Promise<PageResponse<Advisor>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort,
      direction
    });
    const response = await fetch(`${API_BASE}?${params}`);
    if (!response.ok) {
      throw new Error('Failed to fetch advisors');
    }
    return response.json();
  },

  /**
   * Get advisors by brokerage
   */
  getByBrokerage: async (
    brokerageId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction = 'DESC'
  ): Promise<PageResponse<Advisor>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort,
      direction
    });
    const response = await fetch(`${API_BASE}/brokerage/${brokerageId}?${params}`);
    if (!response.ok) {
      throw new Error('Failed to fetch advisors for brokerage');
    }
    return response.json();
  },

  /**
   * Get advisor by ID
   */
  getById: async (id: string): Promise<Advisor> => {
    const response = await fetch(`${API_BASE}/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch advisor');
    }
    return response.json();
  },

  /**
   * Invite a new advisor
   */
  invite: async (advisor: AdvisorInvite): Promise<Advisor> => {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(advisor),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to invite advisor');
    }
    return response.json();
  },

  /**
   * Update advisor
   */
  update: async (id: string, advisor: Advisor): Promise<Advisor> => {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(advisor),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update advisor');
    }
    return response.json();
  },

  /**
   * Update advisor status
   */
  updateStatus: async (id: string, status: string, reason?: string): Promise<Advisor> => {
    const response = await fetch(`${API_BASE}/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status, reason }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update advisor status');
    }
    return response.json();
  },

  /**
   * Resend invitation
   */
  resendInvitation: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/${id}/resend-invitation`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('Failed to resend invitation');
    }
  },

  /**
   * Delete advisor (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete advisor');
    }
  },

  /**
   * Search advisors
   */
  search: async (
    query: string,
    brokerageId?: string,
    page = 0,
    size = 20
  ): Promise<PageResponse<Advisor>> => {
    const params = new URLSearchParams({
      query,
      page: page.toString(),
      size: size.toString(),
    });
    if (brokerageId) {
      params.append('brokerageId', brokerageId);
    }
    const response = await fetch(`${API_BASE}/search?${params}`);
    if (!response.ok) {
      throw new Error('Failed to search advisors');
    }
    return response.json();
  },

  /**
   * Filter advisors by status
   */
  filterByStatus: async (
    brokerageId: string,
    status: string,
    page = 0,
    size = 20
  ): Promise<PageResponse<Advisor>> => {
    const params = new URLSearchParams({
      brokerageId,
      status,
      page: page.toString(),
      size: size.toString(),
    });
    const response = await fetch(`${API_BASE}/filter?${params}`);
    if (!response.ok) {
      throw new Error('Failed to filter advisors');
    }
    return response.json();
  },

  /**
   * Upload advisor photo
   */
  uploadPhoto: async (id: string, file: File): Promise<{ photoUrl: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/${id}/photo`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to upload photo' }));
      throw new Error(error.error || 'Failed to upload photo');
    }
    return response.json();
  },

  /**
   * Delete advisor photo
   */
  deletePhoto: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/${id}/photo`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete photo' }));
      throw new Error(error.error || 'Failed to delete photo');
    }
  },
};
